import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, AlertCircle, Mic, MicOff, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VentModal = ({ isOpen, onClose, onSubmit }) => {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(content.trim());
      setContent('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setContent('');
    stopRecording();
    onClose();
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch {
      toast.error('Microphone access denied. Please allow mic access.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (blob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');

      const response = await axios.post(`${API}/vents/voice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.text) {
        setContent(prev => prev ? `${prev} ${response.data.text}` : response.data.text);
        toast.success('Voice transcribed!');
      }
    } catch {
      toast.error('Failed to transcribe. Try again or type instead.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const characterCount = content.length;
  const maxCharacters = 500;
  const isOverLimit = characterCount > maxCharacters;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          {/* Modal - top-anchored on mobile for keyboard visibility, centered on desktop */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-x-4 top-4 sm:top-1/2 sm:-translate-y-1/2 max-w-lg mx-auto glass rounded-3xl p-5 sm:p-6 z-50 max-h-[90vh] overflow-y-auto"
            data-testid="vent-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-bold text-white">
                <span className="text-vent-secondary">V</span>ENT
              </h2>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="close-vent-modal"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>

            {/* Anonymous notice */}
            <div className="flex items-center gap-2 text-zinc-500 text-sm mb-4">
              <AlertCircle className="w-4 h-4" />
              <span>Your post will be anonymous</span>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="relative">
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Vent on today's topic or suggest next week's topic*"
                  className="w-full h-28 sm:h-40 bg-black/40 border border-white/10 rounded-2xl p-4 pr-14 text-white placeholder-zinc-600 resize-none focus:outline-none focus:border-vent-primary/50 transition-colors"
                  data-testid="vent-textarea"
                  autoFocus
                />

                {/* Mic button - inside textarea corner */}
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleMicClick}
                  disabled={isTranscribing}
                  className={`absolute bottom-3 right-3 p-2.5 rounded-xl transition-all ${
                    isRecording
                      ? 'bg-red-500/20 text-red-400 animate-pulse'
                      : isTranscribing
                      ? 'bg-vent-primary/20 text-vent-primary'
                      : 'bg-white/5 text-zinc-500 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid="voice-record-btn"
                >
                  {isTranscribing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="w-5 h-5" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </motion.button>
              </div>

              {/* Recording indicator */}
              {isRecording && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="text-red-400 text-xs mt-2 flex items-center gap-1.5"
                >
                  <span className="w-2 h-2 bg-red-500 rounded-full" />
                  Recording... tap mic to stop
                </motion.p>
              )}

              {/* Character count + footnote */}
              <div className="flex items-center justify-between mt-3 mb-2">
                <span className={`text-sm ${isOverLimit ? 'text-red-500' : 'text-zinc-500'}`}>
                  {characterCount}/{maxCharacters}
                </span>
                {isOverLimit && (
                  <span className="text-red-500 text-sm">Too many characters</span>
                )}
              </div>

              <p className="text-zinc-600 text-xs mb-3 italic" data-testid="suggest-footnote">
                *topic with most engagement will be selected
              </p>

              {/* Submit button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={!content.trim() || isOverLimit || isSubmitting || isRecording}
                className={`w-full py-4 rounded-2xl font-heading font-bold tracking-widest text-sm uppercase flex items-center justify-center gap-2 transition-all ${
                  !content.trim() || isOverLimit || isSubmitting || isRecording
                    ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                    : 'bg-vent-primary text-white hover:shadow-glow-purple'
                }`}
                data-testid="vent-submit-btn"
              >
                {isSubmitting ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    POST
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default VentModal;
